import React, { useState, useEffect } from 'react';
import AuthenticatedHeader from '../../components/ui/AuthenticatedHeader';
import NavigationBreadcrumbs from '../../components/ui/NavigationBreadcrumbs';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CompanyInformationTab from './components/CompanyInformationTab';
import ContactDetailsTab from './components/ContactDetailsTab';
import AccountSettingsTab from './components/AccountSettingsTab';
import ProfileCompletionCard from './components/ProfileCompletionCard';
import { getUserProfile, updateUserProfile } from '../../services/profileService';
import { useAuth } from '../../contexts/AuthContext';

const PartnerProfile = () => {
  const { user, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('company');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [errors, setErrors] = useState({});
  const [completionPercentage, setCompletionPercentage] = useState(75);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [formData, setFormData] = useState({
    businessName: '',
    taxId: '',
    industryType: '',
    businessType: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    website: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    mobile: '',
    jobTitle: '',
    emailNotifications: true,
    smsNotifications: false,
    weeklySummary: true,
    marketingEmails: false,
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    dashboardLayout: 'detailed',
    notificationFrequency: 'instant',
    timezone: 'Pacific/Honolulu',
    autoSaveDrafts: true,
    showTips: true,
    twoFactorEnabled: false,
    loginAlerts: true,
    suspiciousActivityAlerts: true,
    sessionTimeout: true,
  });

  const [originalFormData, setOriginalFormData] = useState({ ...formData });

  const tabs = [
    { id: 'company', label: 'Company Information', icon: 'Building' },
    { id: 'contact', label: 'Contact Details', icon: 'User' },
    { id: 'settings', label: 'Account Settings', icon: 'Settings' },
  ];

  // Load profile data on component mount
  useEffect(() => {
    // Wait for authentication to finish loading
    if (authLoading) return;
    
    // Only load profile if user is authenticated
    if (user) {
      loadProfileData();
    }
  }, [authLoading, user]);

  const loadProfileData = async () => {
    try {
      setIsLoading(true);
      const result = await getUserProfile();

      if (result?.error) {
        console.error('Failed to load profile:', result?.error);
        setSaveError('Failed to load profile data');
        return;
      }

      if (result?.data || result) {
        // Handle both {data, error} format and direct data format
        const profileData = result?.data || result;
        
        const profileFormData = {
          businessName: profileData?.companyName || '',
          taxId: profileData?.taxId || '',
          industryType: profileData?.industryType || '',
          businessType: profileData?.businessType || '',
          streetAddress: profileData?.businessAddress || '',
          city: '',
          state: '',
          zipCode: '',
          website: profileData?.website || '',
          firstName: profileData?.firstName || '',
          lastName: profileData?.lastName || '',
          email: profileData?.email || '',
          phone: profileData?.phone || '',
          mobile: profileData?.mobile || '',
          jobTitle: profileData?.jobTitle || '',
          emailNotifications: true,
          smsNotifications: false,
          weeklySummary: true,
          marketingEmails: false,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
          dashboardLayout: 'detailed',
          notificationFrequency: 'instant',
          timezone: 'Pacific/Honolulu',
          autoSaveDrafts: true,
          showTips: true,
          twoFactorEnabled: false,
          loginAlerts: true,
          suspiciousActivityAlerts: true,
          sessionTimeout: true,
        };

        setFormData(profileFormData);
        setOriginalFormData({ ...profileFormData });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setSaveError('Failed to load profile data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData);
    setHasUnsavedChanges(hasChanges);
  }, [formData, originalFormData]);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
    setSaveError(null);
    setSaveSuccess(false);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (activeTab === 'company') {
      if (!formData?.businessName?.trim()) newErrors.businessName = 'Business name is required';
      if (!formData?.taxId?.trim()) newErrors.taxId = 'Tax ID is required';
      if (!formData?.streetAddress?.trim()) newErrors.streetAddress = 'Street address is required';
    }
    
    if (activeTab === 'contact') {
      if (!formData?.firstName?.trim()) newErrors.firstName = 'First name is required';
      if (!formData?.lastName?.trim()) newErrors.lastName = 'Last name is required';
      if (!formData?.email?.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
        newErrors.email = 'Email is invalid';
      }
      if (!formData?.phone?.trim()) newErrors.phone = 'Phone number is required';
    }
    
    if (activeTab === 'settings') {
      if (formData?.newPassword || formData?.confirmPassword) {
        if (!formData?.currentPassword) {
          newErrors.currentPassword = 'Current password is required to change password';
        }
        if (formData?.newPassword?.length < 8) {
          newErrors.newPassword = 'Password must be at least 8 characters';
        }
        if (formData?.newPassword !== formData?.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    
    try {
      // Prepare profile updates
      const updates = {
        companyName: formData?.businessName,
        taxId: formData?.taxId,
        industryType: formData?.industryType,
        businessType: formData?.businessType,
        businessAddress: formData?.streetAddress,
        website: formData?.website,
        firstName: formData?.firstName,
        lastName: formData?.lastName,
        email: formData?.email,
        phone: formData?.phone,
        mobile: formData?.mobile,
        jobTitle: formData?.jobTitle,
      };

      // Update profile in Supabase and sync to Salesforce
      const { data, error } = await updateUserProfile(updates);

      if (error) {
        throw new Error(error);
      }

      // Update state with saved data
      setOriginalFormData({ ...formData });
      setIsEditing(false);
      setHasUnsavedChanges(false);
      setSaveSuccess(true);
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
      
    } catch (error) {
      console.error('Error saving profile:', error);
      setSaveError(error?.message || 'Failed to save profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({ ...originalFormData });
    setIsEditing(false);
    setHasUnsavedChanges(false);
    setErrors({});
    setSaveError(null);
    setSaveSuccess(false);
  };

  const renderTabContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="text-muted-foreground">Loading profile...</p>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'company':
        return (
          <CompanyInformationTab
            formData={formData}
            handleChange={handleChange}
            handleSelectChange={handleSelectChange}
            errors={errors}
            isEditing={isEditing}
          />
        );
      case 'contact':
        return (
          <ContactDetailsTab
            formData={formData}
            handleChange={handleChange}
            handleCheckboxChange={handleCheckboxChange}
            errors={errors}
            isEditing={isEditing}
          />
        );
      case 'settings':
        return (
          <AccountSettingsTab
            formData={formData}
            handleChange={handleChange}
            handleSelectChange={handleSelectChange}
            handleCheckboxChange={handleCheckboxChange}
            errors={errors}
            isEditing={isEditing}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AuthenticatedHeader />
      <NavigationBreadcrumbs />
      <main className="main-content pt-20 px-6 pb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-8">
            <div>
              <h1 className="text-3xl font-semibold text-foreground mb-2">Partner Profile</h1>
              <p className="text-muted-foreground">Manage your account information and preferences</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              {!isEditing ? (
                <Button
                  variant="default"
                  iconName="Edit"
                  iconPosition="left"
                  onClick={() => setIsEditing(true)}
                  disabled={isLoading}
                >
                  Edit Profile
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    loading={isSaving}
                    iconName="Check"
                    iconPosition="left"
                    onClick={handleSave}
                  >
                    {isSaving ? 'Saving Changes...' : 'Save Changes'}
                  </Button>
                </>
              )}
            </div>
          </div>

          {hasUnsavedChanges && isEditing && (
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-6 flex items-center gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-warning)" />
              <p className="text-sm text-warning-foreground !text-black">
                You have unsaved changes. Please save or cancel before leaving this page.
              </p>
            </div>
          )}

          {saveError && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6 flex items-center gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
              <p className="text-sm text-destructive">{saveError}</p>
            </div>
          )}

          {saveSuccess && (
            <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6 flex items-center gap-3">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" />
              <p className="text-sm text-success">Profile updated successfully and synced to Salesforce!</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1 space-y-6">
              <ProfileCompletionCard completionPercentage={completionPercentage} />
              
              <div className="bg-card border border-border rounded-lg p-4 elevation-1 hidden lg:block">
                <nav className="space-y-2">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                        activeTab === tab?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon name={tab?.icon} size={18} />
                      <span>{tab?.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="lg:hidden mb-6">
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                        activeTab === tab?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted bg-card border border-border'
                      }`}
                    >
                      <Icon name={tab?.icon} size={16} />
                      <span>{tab?.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border elevation-1 overflow-hidden">
                <div className="p-6 border-b border-border bg-muted/30">
                  <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                    <Icon name={tabs?.find(t => t?.id === activeTab)?.icon || 'Settings'} size={20} />
                    {tabs?.find(t => t?.id === activeTab)?.label}
                  </h2>
                </div>
                <div className="p-6">
                  {renderTabContent()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PartnerProfile;